import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';

/**
 * Single-line text that is cut off with an ellipsis, and scrolls like a
 * ticker — while the host is hovered, or permanently with `autoplay` — so a
 * truncated agent name or creator name can be read without a tooltip.
 *
 * Put the directive on the hover surface (a card, a row) and mark the text
 * elements inside it with `[appMarqueeOnHover]="'.selector'"` — default
 * `.marquee`. Every marked element must already be a single-line clipped box
 * (`white-space: nowrap; overflow: hidden; text-overflow: ellipsis`); the
 * directive adds nothing to elements whose text fits.
 *
 * How it scrolls: the element's own `scrollLeft` is driven with
 * requestAnimationFrame (outside the Angular zone, no change detection) —
 * no inner wrapper, no layout change, the ellipsis is simply switched to
 * `clip` while the text is moving and comes back when it stops. Timeline:
 * short start delay, glide to the end, hold, glide back, hold, repeat.
 * Respects `prefers-reduced-motion` (no movement, ellipsis stays).
 *
 * `[appMarqueeAutoplay]="true"` starts the ticker as soon as the fonts are
 * loaded and keeps it running; hover then makes no difference.
 */
@Directive({
  selector: '[appMarqueeOnHover]',
})
export class MarqueeOnHoverDirective implements AfterViewInit, OnDestroy {
  /** CSS selector of the text elements inside the host that may scroll. */
  @Input('appMarqueeOnHover') selector = '';
  /** Run permanently instead of only while hovered. */
  @Input() appMarqueeAutoplay = false;

  private static readonly SPEED_PX_PER_S = 36;
  private static readonly START_DELAY_MS = 350;
  private static readonly HOLD_MS = 1100;

  /** Unterhalb dieser Strecke gibt es nichts zu fahren — die Ellipse ist
   *  dann schon der ganze Unterschied. */
  private static readonly MIN_TRAVEL_PX = 1;

  private frame = 0;
  private running: { el: HTMLElement; distance: number }[] = [];
  private autoplayTimer: ReturnType<typeof setTimeout> | undefined;

  /** Überlauf in px, **subpixelgenau**.
   *
   *  `scrollWidth` ist eine ganze Zahl. Ein Text, der um einen Bruchteil
   *  eines Pixels übersteht, meldet dort also NULL Überlauf — der Browser
   *  kürzt ihn aber trotzdem und nimmt dafür echte Zeichen weg, weil die
   *  Ellipse selbst Platz braucht. Genau so stand eine Agent-Überschrift
   *  dauerhaft als „… (flexibl…" da, ohne dass die Laufschrift je ansprang:
   *  326,1px Text in einer 326px-Spalte (Marcel, 23.09.2026).
   *
   *  Eine Range über den Inhalt misst die echte Breite, auch wenn der Text
   *  in einem inneren `<span>` steckt (Verlaufsschrift der Agent-Kachel).
   *
   *  Verglichen wird gegen die **Inhaltsbreite**, nicht gegen `clientWidth`:
   *  letzteres schließt den Innenabstand ein, der Text beginnt aber erst
   *  dahinter. Bei den Wissensbasis-Kacheln (`padding: 5px 10px`) fehlten
   *  dadurch genau 20px Strecke und die Laufschrift hielt zwei Zeichen vor
   *  dem Ende an. */
  private static overflowOf(el: HTMLElement): number {
    const range = document.createRange();
    range.selectNodeContents(el);
    const width = range.getBoundingClientRect().width;
    const style = getComputedStyle(el);
    const inner =
      el.clientWidth -
      (parseFloat(style.paddingLeft) || 0) -
      (parseFloat(style.paddingRight) || 0);
    return width - inner;
  }

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    if (!this.appMarqueeAutoplay) return;
    // Overflow can only be measured with the final font metrics.
    const fontsReady = (document as any).fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => {
      this.autoplayTimer = setTimeout(() => this.start(), 200);
    });
  }

  @HostListener('mouseenter')
  onEnter(): void {
    if (this.running.length) return; // autoplay already running
    this.start();
  }

  @HostListener('mouseleave')
  onLeave(): void {
    if (this.appMarqueeAutoplay) return;
    this.stop();
  }

  ngOnDestroy(): void {
    clearTimeout(this.autoplayTimer);
    this.stop();
  }

  private start(): void {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const selector = this.selector || '.marquee';
    // Die Schwelle liegt bewusst weit unter 1px: gekürzt wird schon bei
    // einem Bruchteil, und dann sollen die verdeckten Zeichen sichtbar
    // werden. Der winzige Rest fängt nur Rundungsrauschen ab.
    const overflowing = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>(selector)
    )
      .map((el) => ({ el, distance: MarqueeOnHoverDirective.overflowOf(el) }))
      .filter((entry) => entry.distance > 0.05);
    if (!overflowing.length) return;

    this.stop();
    this.running = overflowing;
    // `clip` statt `ellipsis` gibt die Zeichen frei, die die Ellipse
    // verdeckt hat. Bei knappem Überlauf ist damit schon alles lesbar und
    // es wird gar nicht gefahren — siehe MIN_TRAVEL_PX in `animate`.
    for (const { el } of overflowing) el.style.textOverflow = 'clip';
    this.zone.runOutsideAngular(() => this.animate(performance.now()));
  }

  private animate(start: number): void {
    const { SPEED_PX_PER_S, START_DELAY_MS, HOLD_MS } = MarqueeOnHoverDirective;
    const tick = (now: number) => {
      const elapsed = now - start - START_DELAY_MS;
      for (const { el, distance } of this.running) {
        // Zu kurz zum Fahren: die aufgehobene Ellipse zeigt bereits alles.
        if (distance < MarqueeOnHoverDirective.MIN_TRAVEL_PX) {
          el.scrollLeft = 0;
          continue;
        }
        const travel = (distance / SPEED_PX_PER_S) * 1000;
        const cycle = 2 * (travel + HOLD_MS);
        let position = 0;
        if (elapsed > 0) {
          const t = elapsed % cycle;
          if (t < travel) position = (t / travel) * distance;
          else if (t < travel + HOLD_MS) position = distance;
          else if (t < 2 * travel + HOLD_MS) position = distance - ((t - travel - HOLD_MS) / travel) * distance;
          else position = 0;
        }
        el.scrollLeft = position;
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  private stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    for (const { el } of this.running) {
      el.scrollLeft = 0;
      el.style.textOverflow = '';
    }
    this.running = [];
  }
}
